<template>
  <DashboardCard style="padding: 0">
    <template #title>
      <div class="tw-text-gray-900 tw-pt-6 tw-px-6">{{ title }}</div>
    </template>
    <div class="tw-px-3 tw-pb-3">
      <!-- Loading -->
      <div v-if="$apollo?.loading">
        <JobsCardItemSkeleton />
        <JobsCardItemSkeleton />
        <JobsCardItemSkeleton />
      </div>

      <!-- Error -->
      <template v-else-if="$apollo?.error">
        {{ $t('An error occurred while loading your jobs.') }}
      </template>

      <!-- Result -->
      <BaseShowMore v-else-if="jobQueryData" collapsable>
        <template
          v-if="
            jobQueryData.activeAccount.jobs.data.length === 0 && user.hasPermission('create-jobs')
          "
        >
          <div class="tw-px-3 tw-pb-3">
            <slot name="no-results" />
          </div>
        </template>

        <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
        <list-animation>
          <JobsCardItem
            v-for="job in jobQueryData.activeAccount.jobs.data"
            :key="job.localId"
            :job="job"
            :current-job-step="job.completedSteps"
            :is-job-approved="job.approved"
            :is-job-available="job.available"
            :is-job-open="job.open"
            :is-job-completed="job.completed"
            :is-job-created="job.created"
            :is-job-public="job.public"
            :is-job-for-marketplace="job.forMarketplace"
            :is-job-published="job.published"
            :is-job-removed="job.removed"
            :job-dialog-amount="job.dialogsAmount"
            :job-bills-amount="job.billsAmount"
            :job-hires="job.hires"
            :job-hires-amount="job.hiresAmount"
            :job-id="job.localId"
            :job-name="job.name"
            :job-offers-amount="job.offersAmount"
            :job-dates="job.formattedJobDates"
            :is-job-paused="job.paused"
            :button-link="buttonLink(job.localId)"
            :button-label="buttonLabel"
            :unapproved-bills="job.unapprovedBillCount"
            :approved-bills="job.approvedBillCount"
            :unpaid-invoices="job.notPaidOrInstaPaidBillCount"
            :unanswered-dialogs="job.unansweredDialogs.paginatorInfo.total"
            :fresh-dialogs="job.freshDialogs.paginatorInfo.total"
            :closed-dialogs="job.closedDialogs.paginatorInfo.total"
            :shortlisted-dialogs="job.shortlistedDialogs.paginatorInfo.total"
            :type="queryFilter"
            :show-custom-fields="
              (jobQueryData.activeAccount.viewerCanManageFields &&
                jobQueryData.activeAccount.hasFieldsDefined) ||
              jobQueryData.activeAccount.hasTeamMembers
            "
            :shared-with-trusted-contacts="job.forContacts"
            :shared-with-recruiters="job.forRecruiters"
          />
        </list-animation>

        <div
          v-if="jobQueryData.activeAccount.jobs.paginatorInfo.hasMorePages"
          class="m-5 text-right"
        >
          <a :href="'/jobs/created/active?filter=' + queryFilter">
            {{ $t('See all your jobs') }}
          </a>
        </div>
        <template #button="{ buttonText }">
          <BaseShowMoreButton class="tw-ml-2" :button-text="buttonText" />
        </template>
      </BaseShowMore>
    </div>
  </DashboardCard>
</template>

<script>
import gql from 'graphql-tag'
import JobsCardItemSkeleton from '@/components/JobsCardItemSkeleton.vue'
import JobsCardItem from '@/components/JobsCardItem.vue'
import BaseShowMore from '@/components/BaseShowMore.vue'
import BaseShowMoreButton from '@/components/BaseShowMoreButton.vue'
import DashboardCard from '@/components/DashboardCard.vue'

export default {
  components: {
    DashboardCard,
    JobsCardItemSkeleton,
    JobsCardItem,
    BaseShowMore,
    BaseShowMoreButton,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    queryFilter: {
      type: String,
      required: false,
      default: 'UNFILLED',
    },
    hideIfNoResult: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      jobQueryData: null,
    }
  },
  apollo: {
    jobQueryData: {
      query: gql`
        query jobQuery($queryFilter: YjobFilterType!) {
          jobQueryData: me {
            globalId
            activeAccount {
              globalId
              ... on Company {
                hasTrustedContacts
                hasTeamMembers
                viewerCanManageFields
                hasFieldsDefined
                jobs(first: 25, type: $queryFilter) {
                  paginatorInfo {
                    hasMorePages
                  }
                  data {
                    globalId
                    localId
                    name
                    hires {
                      data {
                        globalId
                        threadId
                        dateStart
                        dateEnd
                        freelancer {
                          globalId
                          name
                          primaryProfile {
                            localId
                            url
                          }
                        }
                      }
                    }
                    unansweredDialogs: dialogs(type: UNANSWERED) {
                      paginatorInfo {
                        total
                      }
                    }
                    freshDialogs: dialogs(type: FRESH) {
                      paginatorInfo {
                        total
                      }
                    }
                    closedDialogs: dialogs(type: CLOSED) {
                      paginatorInfo {
                        total
                      }
                    }
                    shortlistedDialogs: dialogs(type: SHORTLISTED) {
                      paginatorInfo {
                        total
                      }
                    }
                    billsAmount
                    unapprovedBillCount
                    approvedBillCount
                    notPaidOrInstaPaidBillCount
                    hiresAmount
                    offersAmount
                    dialogsAmount
                    open
                    approved
                    published
                    publicForContacts
                    forMarketplace
                    forContacts
                    forRecruiters
                    public
                    removed
                    created
                    available
                    completedSteps
                    completed
                    paused
                    formattedJobDates
                  }
                }
              }
            }
          }
        }
      `,
      variables() {
        return { queryFilter: this.queryFilter }
      },
    },
  },

  computed: {
    buttonLabel() {
      // null so that the default is picked up
      return this.queryFilter !== 'DRAFT' ? $t('View job') : null
    },
  },
  methods: {
    buttonLink(jobId) {
      switch (this.queryFilter) {
        case 'UNFILLED':
        case 'UNFILLED_WITH_DRAFTS':
          return `/job/${jobId}/conversation-and-bids?type=fresh&orderBy=relevance`
        case 'FILLED':
          return `/job/${jobId}/contract`
        default:
          // null so that the default is picked up
          return null
      }
    },
  },
}
</script>
